import { type ReferentPriceType } from '@strata/constants';

export interface ConversionCleanlinessPreview {
    wouldBeClean?: boolean;
    wouldUnmanage?: boolean;
    cleanReason?: string | null;
    disposition?: string | null;
    rejectCode?: string | null;
    outcome?: string | null;
    payableProductServiceId?: string | null;
    hasActiveAuth?: boolean;
    supplierNpi?: string | null;
    offerRate?: string | number | null;
    referentPriceType?: ReferentPriceType | null;
    conversionPreviewId?: string | null;
    rerouteDuringRouteSim?: boolean;
}

export interface ConversionCleanlinessPreviewRequest {
    payerId: string;
    treatmentId: string;
    referralId?: string;
    segmentId: string;
    memberId?: string;
    patientFirstName?: string;
    patientLastName?: string;
    patientDob?: string;
    prescriberNpi?: string;
    productServiceIds: string[];
    productServiceOid: string;
    asOfDate: string;
    parentSupplierNpi?: string;
    lineOfBusiness?: string;
    lobSegment?: string;
    memberState?: string;
    routeCheck?: {
        segmentId: string;
        memberId?: string;
        patientFirstName?: string;
        patientDob?: string;
        productServiceOid: string;
        isBrand: boolean;
        benefit: string;
        memberBenefitGroup?: string | null;
        patientZip?: string;
        supplierNpi?: string | null;
        prescriberNpi?: string;
        placeOfService?: string;
        therapeuticCategory?: string;
        preferredPharmacyOffer?: {
            parentPharmacyNpi: string;
            routeCode: string;
            allowRouteOverride: boolean;
        } | null;
    };
}

export const conversionCleanlinessQuery = ({
    payerId,
    treatmentId,
    referralId,
    segmentId,
    memberId,
    patientFirstName,
    patientLastName,
    patientDob,
    prescriberNpi,
    productServiceIds,
    productServiceOid,
    asOfDate,
    parentSupplierNpi,
    lineOfBusiness,
    lobSegment,
    memberState,
    routeCheck,
}: ConversionCleanlinessPreviewRequest) => ({
    url: `${payerId}/treatment_cohort/conversion-cleanliness`,
    method: 'POST' as const,
    params: {
        segment_id: segmentId,
    },
    body: {
        payer_id: payerId,
        treatment_id: treatmentId,
        referral_id: referralId ?? null,
        member_id: memberId,
        patient_first_name: patientFirstName,
        patient_last_name: patientLastName,
        patient_dob: patientDob,
        prescriber_npi: prescriberNpi,
        product_service_ids: productServiceIds,
        product_service_oid: productServiceOid,
        as_of_date: asOfDate,
        parent_supplier_npi: parentSupplierNpi ?? null,
        line_of_business: lineOfBusiness ?? null,
        lob_segment: lobSegment ?? null,
        member_state: memberState ?? null,
        ...(routeCheck && {
            route_check: {
                payer_id: payerId,
                segment_id: routeCheck.segmentId,
                member_id: routeCheck.memberId,
                patient_first_name: routeCheck.patientFirstName,
                patient_dob: routeCheck.patientDob,
                product_service_oid: routeCheck.productServiceOid,
                is_brand: routeCheck.isBrand,
                benefit: routeCheck.benefit,
                member_benefit_group: routeCheck.memberBenefitGroup,
                patient_zip: routeCheck.patientZip,
                supplier_npi: routeCheck.supplierNpi,
                offer_rate: null,
                prescriber_npi: routeCheck.prescriberNpi,
                prescriber_practice_npi: null,
                place_of_service: routeCheck.placeOfService,
                therapeutic_category: routeCheck.therapeuticCategory,
                check_only: true,
                servicing_supplier: null,
                preferred_pharmacy_offer: routeCheck.preferredPharmacyOffer
                    ? {
                          parent_pharmacy_npi: routeCheck.preferredPharmacyOffer.parentPharmacyNpi,
                          route_code: routeCheck.preferredPharmacyOffer.routeCode,
                          allow_route_override:
                              routeCheck.preferredPharmacyOffer.allowRouteOverride,
                          offer_rate: null,
                      }
                    : null,
            },
        }),
    },
});
